import { useInView } from '../../../hooks/useInView';

const cities = [
  'Oviedo', 'Orlando', 'Tampa', 'Jacksonville', 'Miami', 'Fort Lauderdale',
  'Kissimmee', 'Sanford', 'Daytona Beach', 'Gainesville', 'Sarasota',
  'Cape Coral', 'Melbourne', 'Ocala', 'Lakeland', 'St. Petersburg',
  'Clearwater', 'Tallahassee', 'Fort Myers', 'Boca Raton',
];

export default function ServiceAreas() {
  const { ref, inView } = useInView();

  return (
    <section className="relative overflow-hidden bg-[#080808]" id="service-areas">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/12 to-transparent" />

      <div
        ref={ref as React.RefObject<HTMLDivElement>}
        className={`max-w-7xl mx-auto px-8 lg:px-20 py-36 transition-all duration-800 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
          {/* Left */}
          <div>
            <p className="font-sans text-[0.58rem] tracking-[0.45em] uppercase text-gold mb-6 font-medium">Service Areas</p>
            <h2 className="font-serif font-bold text-white leading-tight mb-8" style={{ fontSize: 'clamp(2.5rem, 4.5vw, 4.5rem)', letterSpacing: '-0.025em' }}>
              Serving Oviedo, Orlando,<br />
              &amp;{' '}
              <em className="not-italic text-gold">Central Florida</em>
            </h2>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-px bg-gold" />
              <div className="w-4 h-px bg-gold/40" />
            </div>
            <p className="font-sans text-sm text-white/42 leading-[1.95] mb-10 max-w-md">
              Headquartered in Oviedo, Central Florida, our broadcast-grade production team is equipped for on-location shoots across the entire state of Florida. Available for travel productions nationwide and internationally.
            </p>

            {/* City grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-10">
              {cities.map((city) => (
                <div key={city} className="flex items-center gap-2 group cursor-default py-1">
                  <div className="w-1 h-1 bg-gold/40 group-hover:bg-gold flex-shrink-0 transition-colors duration-300" />
                  <span className="font-sans text-[0.7rem] text-white/38 group-hover:text-white/65 transition-colors duration-300 whitespace-nowrap">{city}</span>
                </div>
              ))}
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-3 border border-gold/30 px-5 py-3 group hover:border-gold transition-colors duration-300 cursor-default">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-plane-line text-gold text-base" />
                </div>
                <div>
                  <p className="font-sans text-[0.62rem] tracking-[0.15em] uppercase text-gold font-medium">Travel Available</p>
                  <p className="font-sans text-[0.58rem] text-white/32">Nationwide &amp; International</p>
                </div>
              </div>
              <div className="flex items-center gap-3 border border-white/8 px-5 py-3 cursor-default">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className="ri-map-pin-2-line text-gold text-base" />
                </div>
                <div>
                  <p className="font-sans text-[0.62rem] tracking-[0.15em] uppercase text-white/55 font-medium">Headquarters</p>
                  <p className="font-sans text-[0.58rem] text-white/32">Oviedo, Central Florida</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Map */}
          <div className="flex flex-col gap-4">
            <div className="relative w-full overflow-hidden border border-white/8" style={{ height: '460px' }}>
              <iframe
                title="Immanuel Production Group - Central Florida Service Area Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d224688.05!2d-81.35!3d28.67!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e773d8fecdbc77%3A0xac3b2063ca5bf9e!2sCentral+Florida!5e0!3m2!1sen!2sus!4v1700000000001!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'grayscale(95%) invert(92%) contrast(95%) brightness(0.8)' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="absolute inset-0 pointer-events-none" style={{ background: 'rgba(2,2,2,0.15)' }} />
              {/* Location pin overlay */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-full pointer-events-none">
                <div className="w-4 h-4 bg-gold rounded-full border-2 border-[#020202] animate-pulse-gold" />
              </div>
            </div>
            <p className="font-sans text-[0.65rem] text-white/30 text-center leading-relaxed italic">
              Corporate video production &amp; broadcast services in Oviedo, Orlando, Tampa, Miami, and throughout Florida.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
